/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useClassOfServiceForm.ts
 🗓️ Created: 2025-01-27
 🧩 Description:
    Hook for managing Class of Service form state and submission.
────────────────────────────────────────────────────────────────*/

import { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useI18n } from '../../../hooks/useI18n';
import { createClassOfService, updateClassOfService } from '../api/classOfServicesApi';
import { FormState, ValidationErrors } from '../types';
import { validateClassOfServiceForm } from '../utils/validators';
import { getDefaultFormState } from '../utils/formDefaults';

/**
 * Hook for Class of Service form management
 */
export function useClassOfServiceForm(
  initialFormData: FormState | null,
  id: string | undefined,
  isEditMode: boolean
) {
  const { showSuccess, showError, showInfo } = useNotifications();
  const { t } = useI18n();
  const showErrorRef = useRef(showError);
  const showInfoRef = useRef(showInfo);

  const defaultFormState = useMemo(() => getDefaultFormState(), []);
  const [formData, setFormData] = useState<FormState>(
    initialFormData || defaultFormState
  );
  const [originalData, setOriginalData] = useState<FormState>(
    initialFormData || defaultFormState
  );
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    showErrorRef.current = showError;
    showInfoRef.current = showInfo;
  }, [showError, showInfo]);

  // Sync with initial data when it changes
  useEffect(() => {
    if (initialFormData) {
      const currentSerialized = JSON.stringify(initialFormData);
      const lastSerialized = JSON.stringify(originalData);

      if (currentSerialized !== lastSerialized) {
        setFormData(initialFormData);
        setOriginalData(JSON.parse(JSON.stringify(initialFormData)));
        setErrors({});
      }
    } else if (!isEditMode && !initialFormData) {
      const defaultState = getDefaultFormState();
      setFormData(defaultState);
      setOriginalData(JSON.parse(JSON.stringify(defaultState)));
      setErrors({});
    }
  }, [initialFormData, isEditMode, originalData]);

  const hasChanges = useMemo(() => {
    if (!isEditMode) {
      return true;
    }

    return (
      formData.name !== originalData.name ||
      formData.description !== originalData.description ||
      formData.featureCodeCosId !== originalData.featureCodeCosId ||
      formData.dialRuleRestrictionId !== originalData.dialRuleRestrictionId ||
      formData.arsId !== originalData.arsId ||
      formData.isMain !== originalData.isMain ||
      formData.enabled !== originalData.enabled ||
      formData.failoverDestinationModule !== originalData.failoverDestinationModule ||
      formData.failoverDestinationData !== originalData.failoverDestinationData
    );
  }, [formData, isEditMode, originalData]);

  const handleChange = useCallback(
    <K extends keyof FormState>(field: K, value: FormState[K]) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      if (errors[field]) {
        setErrors((prev) => {
          const next = { ...prev };
          delete next[field];
          return next;
        });
      }
    },
    [errors]
  );

  const validateForm = useCallback(() => {
    const validationErrors = validateClassOfServiceForm(formData, t);
    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  }, [formData, t]);

  // Handle submit - Always-Active Save Standard: always attempt to save
  const handleSubmit = useCallback(async (): Promise<FormState | undefined> => {
    if (!validateForm()) {
      return undefined;
    }

    setIsSaving(true);
    try {
      const result = isEditMode
        ? await updateClassOfService(id!, formData, t)
        : await createClassOfService(formData, t);

      showSuccess(
        isEditMode
          ? t('classOfServices.messages.updateSuccess')
          : t('classOfServices.messages.createSuccess')
      );

      const trimmedPayload: FormState = {
        ...formData,
        name: formData.name.trim(),
        description: formData.description.trim(),
        featureCodeCosId: formData.featureCodeCosId ?? null,
        dialRuleRestrictionId: formData.dialRuleRestrictionId ?? null,
        arsId: formData.arsId ?? null,
        failoverDestinationModule: formData.failoverDestinationModule || 'terminate',
        failoverDestinationData: formData.failoverDestinationData ?? null,
      };

      setOriginalData(trimmedPayload);
      setFormData(trimmedPayload);

      // Return data with ID for navigation
      return { ...trimmedPayload, id: result.data?.id, uuid: result.data?.uuid } as any;
    } catch (error) {
      const fallbackError = isEditMode
        ? t('classOfServices.messages.updateError')
        : t('classOfServices.messages.createError');
      const message = error instanceof Error ? error.message : fallbackError;
      showErrorRef.current(message);
      throw error;
    } finally {
      setIsSaving(false);
    }
  }, [formData, hasChanges, id, isEditMode, showSuccess, t, validateForm]);

  const handleCancel = useCallback(() => {
    if (!isEditMode) {
      const reset = getDefaultFormState();
      setFormData(reset);
      setErrors({});
      return;
    }

    setFormData({ ...originalData });
    setErrors({});
  }, [isEditMode, originalData]);

  return {
    formData,
    setFormData,
    originalData,
    errors,
    isSaving,
    hasChanges,
    handleChange,
    handleSubmit,
    handleCancel,
    validateForm,
  };
}

